import React, {useCallback, useEffect, useRef, useState} from "react";
import { useAddToCartMutation, useGetCartQuery, useRemoveFromCartMutation } from "@/redux/apis";
import { useSnackbar } from "notistack";
import SnackMessage, {updateSnackMessage} from "../components/product-card/snack-message";
import { useAppDispatch } from "@/redux/hooks";
import { setCartBadge } from "@/redux/badge.center.slice";


export default function useProductCount(
    productId: string,
    productName: string,
    mode: "cart" | "preorder" = "cart",
    options?: {
        initialCount?: number;
    }
) {
    const dispatch = useAppDispatch();
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();

    const initial = typeof options?.initialCount === "number" ? options.initialCount : 0;

    const { data: cartItems, refetch, isLoading } = useGetCartQuery(undefined, {
        refetchOnMountOrArgChange: true,
        refetchOnFocus: true,
        refetchOnReconnect: true,
    });

    const [productCount, setProductCountRaw] = useState<number>(initial);
    const currentCountRef = useRef<number>(initial);
    const lastNotifiedCountRef = useRef<number | null>(initial);

    const [addToCart, { isLoading: isAdding }] = useAddToCartMutation();
    const [removeFromCart, { isLoading: isRemoving }] = useRemoveFromCartMutation();
    const expectedCartType = mode === "preorder" ? 0 : 1;

    const findCurrentCartItem = useCallback((items: typeof cartItems) => items?.find((item) =>
        item.productId.toString() === productId &&
        (item.cartType === undefined || item.cartType === expectedCartType)
    ), [expectedCartType, productId]);

    useEffect(() => {
        if (!cartItems) return;

        const total = cartItems.reduce((sum, it) => sum + it.count, 0);
        dispatch(setCartBadge(total));

        const item = findCurrentCartItem(cartItems);
        const serverCount = item ? item.count : 0;

        setProductCountRaw(serverCount);
        currentCountRef.current = serverCount;

        if (lastNotifiedCountRef.current === null) {
            lastNotifiedCountRef.current = serverCount;
        }
    }, [cartItems, dispatch, findCurrentCartItem]);

    async function setProductCount(nextCount: number, notification = false) {
        const prevCount = currentCountRef.current;

        const currentTotal = cartItems?.reduce((sum, it) => sum + it.count, 0) ?? 0;
        const optimisticTotal = Math.max(0, currentTotal - prevCount + nextCount);
        dispatch(setCartBadge(optimisticTotal));

        try {

            currentCountRef.current = nextCount;
            setProductCountRaw(nextCount);


            if (notification && nextCount > 0) {
                const snackbarKey = `cart-snack-${mode}-${productId}`;

                const updated = updateSnackMessage(snackbarKey, nextCount);

                if (!updated) {
                    enqueueSnackbar("", {
                        key: snackbarKey,
                        anchorOrigin: { horizontal: "right", vertical: "top" },
                        style: { marginTop: 10, marginLeft: 54 },
                        content: (
                            <SnackMessage
                                snackId={snackbarKey}
                                productName={productName}
                                countInCart={nextCount}
                                mode={mode}
                            />
                        ),
                    });
                }
            }

            lastNotifiedCountRef.current = nextCount;

            if (nextCount === 0) {
                await removeFromCart({ ProductId: productId }).unwrap();
            } else {
                await addToCart({ Count: nextCount, ProductId: productId }).unwrap();
            }

        } catch (error) {
            try {
                const updated = await refetch().unwrap();
                const totalCount = updated?.reduce((sum, it) => sum + it.count, 0) ?? 0;
                dispatch(setCartBadge(totalCount));

                const theItem = findCurrentCartItem(updated);
                const serverCount = theItem ? theItem.count : 0;

                setProductCountRaw(serverCount);
                currentCountRef.current = serverCount;
                lastNotifiedCountRef.current = serverCount;
            } catch {}

            console.error("Error updating cart:", error);
        }
    }

    return {
        productCount,
        setProductCount,
        isLoading: isLoading || isAdding || isRemoving,
        isCartLoading: isLoading,
    };
}


